import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import {
  ActiveTestQueuedStateService,
  AGENT_MODEL,
  Device,
  DevicesService,
  FUSION_AGENT_ACTION_NAME,
  FusionTransformationService,
  IDeviceData,
  REFLECTOR_TYPE,
  TabulatorService,
  UserAuth
} from '@nrwl/common-lib';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { find, forEach, isArray, isEmpty, map } from 'lodash';
import { DataSourceService } from '../../service/datatsource.service';
import { DataSource } from '../datasource.entity.widget/datasource.entity.component';
import { MatDialog } from '@angular/material/dialog';
import { AgentSpecComponent } from '../managed-devices/agent-spec/agent-spec.component';
import { TwampReflectorComponent } from './twamp-reflector/twamp-reflector.component';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { JmepReflectorComponent } from './jmep-reflector/jmep-reflector.component';

@Component({
  selector: 'nrwl-endpoints-management',
  templateUrl: './endpoints-management.component.html',
  styleUrls: ['./endpoints-management.component.scss']
})
export class EndpointsManagementComponent implements OnInit, AfterViewInit, OnDestroy {
  isLoading = false;
  actualDataSource: Array<Device>;
  deviceDataSource: Array<Device>;
  hasReadWriteRole = false;
  table: any;
  platform = 'model';
  dataSourceNameImg = 'images/agents-bp.svg';
  currentDataSource: any = [];
  deviceWithMonitors = [];
  dataSourceTableData: any = [];
  deviceAvailable = false;
  private cacheObj: Subscription;
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    private readonly userAuth: UserAuth,
    private readonly tabulatorService: TabulatorService,
    private readonly deviceService: DevicesService,
    private readonly dataSourceService: DataSourceService,
    private readonly transformService: FusionTransformationService,
    private readonly queuedService: ActiveTestQueuedStateService,
    private readonly dialog: MatDialog,
    private readonly navigator: NavigationProvider
  ) {
    this.table = this.tabulatorService.commonTableConfiguration();
    this.table.groupHeader = this.tabulatorService.groupHeader;
    this.table.dataTree = true;
    this.table.dataTreeFilter = true;
    this.table.dataTreeStartExpanded = true;
    this.table.dataTreeSelectPropagate = false;
    this.table.rowHeight = 40;
    this.table.initialSort = [{ column: 'name.label', dir: 'asc' }];
  }

  ngOnInit(): void {
    this.hasReadWriteRole = this.userAuth.isAuthedFor('READ_WRITE');
    this.populateDeviceData();
  }

  ngAfterViewInit() {
    this.deviceAvailable = true;
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  hasData(): boolean {
    return isArray(this.currentDataSource) && !isEmpty(this.currentDataSource);
  }

  populateDeviceData(): void {
    this.cacheObj = this.deviceService.reflectorsDeviceCache
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        this.actualDataSource = data;
        this.deviceDataSource = this.getEndpointsData(data);
        this.getTranformedData$();
      });
    this.queuedService.usedTestCountCache.pipe(takeUntil(this.unsubscribe)).subscribe(monitors => {
      if (monitors?.length) {
        this.deviceWithMonitors = monitors;
        this.getTranformedData$();
      }
    });
  }

  getEndpointsData(devices: Device[]): any {
    const filterData = this.removeDuplicateDeviceObjects(devices);
    return this.dataSourceService.sortDataByName(filterData);
  }

  getTranformedData$(): void {
    this.currentDataSource = this.transformResponse(this.deviceDataSource);
    this.assignData();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  assignData(): void {
    this.dataSourceTableData = this.transformService.transformData(
      'endpoints-management',
      this.currentDataSource
    );
    this.table.columns = this.dataSourceTableData.column;
    this.table.data = this.dataSourceTableData.data;
    this.table = Object.assign({}, this.table);
  }

  removeDuplicateDeviceObjects(deviceObj: Device[]): Device[] {
    return deviceObj.filter((device, index) => {
      return (
        index === deviceObj.findIndex(o => device.deviceData.uniqueId === o.deviceData.uniqueId)
      );
    });
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformResponse(dataIn: Array<Device>): any[] {
    const tranformedData = [];
    const deviceData = map(dataIn, data => data.deviceData);
    if (!isEmpty(deviceData) && isArray(deviceData)) {
      forEach(deviceData, (item: IDeviceData) => {
        if (
          item.name &&
          item.name.indexOf('_DESTROYED_') < 0 &&
          item.type === DataSource.GIGA_TEST
        ) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const result: any = {};
          const actions = this.transformService.getEndpointsActions(item);
          switch (item.type) {
            case DataSource.GIGA_TEST:
              result.id = item.uniqueId;
              result.name = this.getIconLabelName(item.nameId, this.dataSourceNameImg, item);
              result.type = this.transformService.getDeviceTypeInfo(
                this.getParamsValueByName(item.params, this.platform)
              );
              result.dataType = item.type;
              result.status = item.status;
              result.location = item.location ? item.location : '--';
              result.state = item.state;
              result.dataItem = item;
              result.ipAddress = item.model.model.toLowerCase().includes('jmep')
                ? { label: item.ipaddress }
                : { label: item.mgmtIPaddress };
              result.testAssigned = item.phyId
                ? ''
                : this.getTestAssignedDatasource(
                    this.getActiveTestQueuedStatus(this.deviceWithMonitors, item.id),
                    item.nameId
                  );
              result.reflectorData = this.getSinglePortReflectorText(item);
              result.children = this.getTableChildren(item);
              result.actions = actions;
              break;
          }
          tranformedData.push(result);
        }
      });
    }
    return tranformedData;
  }

  getSinglePortReflectorText(item: IDeviceData): { icon: string; text: string } {
    const reflectorData = { emit: true, icon: '', text: '', id: 'none' };
    if (!item.phyId && item.status === 'online' && item.agentType !== AGENT_MODEL.MAP2100) {
      const isTestRunning =
        item.activity?.status === 'running' &&
        (item.activity.tests['twamp-reflector']?.length > 0 ||
          item.activity.tests['twamp-light-reflector']?.length > 0);
      const isJMEPAgent =
        item.agentType === AGENT_MODEL.JMEP_10G || item.agentType === AGENT_MODEL.JMEP_1G;
      if (isTestRunning) {
        reflectorData.icon = 'mdi-redo';
        reflectorData.text = isJMEPAgent ? 'Monitor Reflector' : 'Monitor Reflector(s)';
        reflectorData.id = 'endpoints_running';
      } else {
        reflectorData.icon = 'mdi-play';
        reflectorData.text = isJMEPAgent ? 'Start Reflector' : 'Start Reflector(s)';
        reflectorData.id = 'endpoints_stopped';
      }
    }
    return reflectorData;
  }

  getMulitPortReflectorText(
    deviceData: IDeviceData,
    phyId: string
  ): { icon: string; text: string } {
    const reflectorData = { emit: true, icon: '', text: '', id: 'none' };
    if (deviceData.status === 'online') {
      const isTestRunning =
        deviceData.activity?.[phyId]?.status === 'running' &&
        (deviceData.activity?.[phyId]?.tests['twamp-reflector']?.length > 0 ||
          deviceData.activity?.[phyId]?.tests['twamp-light-reflector']?.length > 0);
      if (isTestRunning) {
        reflectorData.icon = 'mdi-redo';
        reflectorData.text = 'Monitor Reflector(s)';
        reflectorData.id = 'endpoints_running';
      } else {
        reflectorData.icon = 'mdi-play';
        reflectorData.text = 'Start Reflector(s)';
        reflectorData.id = 'endpoints_stopped';
      }
    }
    return reflectorData;
  }

  getTableChildren(deviceData: IDeviceData): any {
    const children = [];
    if (deviceData.capabilities?.['phy-list']) {
      deviceData.capabilities['phy-list'].forEach(item => {
        const agentId =
          item['phy-id'] === 'p1' ? deviceData.uniqueId + '-port1' : deviceData.uniqueId + '-port2';
        children.push({
          id: agentId,
          name: this.getIconLabelName(item.name, this.dataSourceNameImg, deviceData),
          type: this.transformService.getDeviceTypeInfo(
            this.getParamsValueByName(deviceData.params, this.platform)
          ),
          dataType: deviceData.type,
          status: deviceData.status,
          state: deviceData.state,
          ipAddress: { label: item['default-test-ip'] || item['external-test-ip'] },
          testAssigned: this.getTestAssignedDatasource(
            this.getActiveTestQueuedStatus(this.deviceWithMonitors, agentId),
            deviceData.nameId
          ),
          reflectorData: this.getMulitPortReflectorText(deviceData, item['phy-id'])
        });
      });
      return children;
    }
    return null;
  }

  //eslint-disable-next-line
  getActiveTestQueuedStatus(deviceWithMonitors: any, id: string): any {
    return deviceWithMonitors.find(device => device.deviceData.id === id);
  }

  // eslint-disable-next-line
  getTestAssignedDatasource(data: any, deviceName: string): any {
    const dataSource = [];
    const testAssignedData = data?.queueStatus;
    for (const key in testAssignedData) {
      if (testAssignedData[key]?.total) {
        dataSource.push({
          type: key,
          used: testAssignedData[key]?.used,
          max: testAssignedData[key]?.total,
          className: this.tabulatorService.getTestTypeImage(key),
          maxCount: testAssignedData['maxCount'],
          overalUsed: testAssignedData['overall_total_used'],
          overalPercentage: Number.isNaN(testAssignedData['overall_percentage'])
            ? -1
            : testAssignedData['overall_percentage'],
          deviceName: deviceName,
          hasOveralPercentage: Number.isInteger(testAssignedData['overall_percentage'])
            ? 'block'
            : 'none',
          showProgressBar: Number.isInteger(testAssignedData['overall_percentage'])
            ? 'none'
            : 'block',
          hideView: key === REFLECTOR_TYPE.FULL || key === REFLECTOR_TYPE.LIGHT
        });
      }
    }
    return dataSource;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getIconLabelName(
    labelName: string,
    iconName: string,
    deviceData: IDeviceData
  ): { icon: string; label: string; sublabel: string } {
    return {
      icon: iconName,
      label: labelName,
      sublabel: this.transformService.getEsamName(deviceData, this.deviceDataSource)
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getParamsValueByName(params: any, name: string): any {
    const paramsObj = find(params, ['name', name]);
    return paramsObj && paramsObj.value ? paramsObj.value : '';
  }

  // eslint-disable-next-line
  sendPopupAction(event: any): void {
    this.goToTestListPage(event[0], true);
  }

  // eslint-disable-next-line
  goToTestListPage(testInfo?: any, isRunning = false): void {
    const expression = isRunning
      ? `device=${testInfo.deviceName}&testtype=${testInfo.type}&status=Running`
      : `device=${testInfo.name.label}`;

    this.navigator
      .addRootParam('test_list_filter', expression)
      .navigateToDashboard('test-list-management', {
        queryParams: { layeredSource: ['Device'], isFilterApplied: 'false' }
      });
  }

  // eslint-disable-next-line
  onActionClick(action: any): any {
    switch (action.label) {
      case FUSION_AGENT_ACTION_NAME.AGENT_SPECS:
        this.openAgentDetailsDialog(action.rowData);
        break;
      case 'singleSelect':
        {
          const deviceItem = this.actualDataSource.find(data => data.getId() === action.rowData.id);
          if (!deviceItem.getModel().toLowerCase().includes('jmep')) {
            //twamp reflectors
            this.dialog.open(TwampReflectorComponent, {
              width: '74rem',
              disableClose: true,
              data: deviceItem
            });
          } else {
            this.dialog.open(JmepReflectorComponent, {
              width: '50rem',
              minHeight:'20rem',
              disableClose: true,
              data: deviceItem
            });
          }
        }
        break;
    }
  }

  // eslint-disable-next-line
  openAgentDetailsDialog(data: any): void {
    data.dataItem['model']['model'] = data.dataItem.model.model ?? '';
    data.dataItem['model']['version'] = data.dataItem.model.version ?? '';
    const dialogData = { deviceName: data.dataItem.name, data: data.dataItem };
    this.dialog.open(AgentSpecComponent, {
      width: '66rem',
      disableClose: true,
      data: dialogData
    });
  }
}
